/**
 * Base64 <-> bytes, shared by encryption and device storage.
 *
 * Uses Uint8Array.fromBase64 / toBase64 when the runtime has them; otherwise
 * a tight loop over atob's output into a preallocated array, and a chunked
 * String.fromCharCode for encoding (one call on a large array overflows the
 * stack). Never builds one string or array element per byte beyond that.
 */

type NativeBase64Ctor = { fromBase64?: (base64: string) => Uint8Array<ArrayBuffer> };
type NativeBase64Bytes = { toBase64?: () => string };

export function bytesToBase64(bytes: Uint8Array): string {
    const native = (bytes as Uint8Array & NativeBase64Bytes).toBase64;
    if (typeof native === 'function') return native.call(bytes);
    let binary = '';
    const chunkSize = 8192;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + chunkSize, bytes.length)));
    }
    return btoa(binary);
}

export function base64ToBytes(base64: string): Uint8Array<ArrayBuffer> {
    const native = (Uint8Array as unknown as NativeBase64Ctor).fromBase64;
    if (typeof native === 'function') return native.call(Uint8Array, base64);
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}
